import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as PlusIcon } from "icons/plus-in-circle.svg";
import { selectDictionary, selectSettings } from "slices/dictionary/selectors";
import {
  editDictionary,
  addTerm,
  addCategory,
} from "slices/dictionary/dictionarySlice";
import {
  createEmptyCategory,
  createEmptyTerm,
  findTermIndex,
} from "utils/dictionary";
import { entryTypes } from "constants/dictionary";
import PropTypes from "prop-types";

const SubHeader = ({
  selectedRowIndex,
  setSelectedRowIndex,
  setSelectedColumnIndex,
}) => {
  const {
    createdBy,
    createdDate,
    updateBy,
    updatedDate,
    description,
    categories,
  } = useSelector(selectDictionary);
  const { isEditMode } = useSelector(selectSettings);
  const { selectedEntry } = useSelector(selectSettings);
  const dispatch = useDispatch();

  const renderButton = ({ title, icon, onClick, dataTest, disabled }) => (
    <div
      className={`edit_dictionary_page__sub_header__right_part__button_container${
        disabled ? " disabled" : ""
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        data-test={dataTest}
        className="edit_dictionary_page__sub_header__right_part__button"
        disabled={disabled}
      >
        <span className="edit_dictionary_page__sub_header__right_part__button__icon">
          {icon}
        </span>
        <span className="edit_dictionary_page__sub_header__right_part__button__title">
          {title}
        </span>
      </button>
    </div>
  );

  const handleNewTermClick = () => {
    if (!selectedEntry) {
      return;
    }
    if (selectedEntry.addNewTermRow) {
      dispatch(
        addTerm({
          categoryId: selectedEntry.categoryId,
          indexToPlace: selectedRowIndex,
          term: createEmptyTerm(),
        })
      );
      return;
    }
    if (selectedEntry.type === entryTypes.CATEGORY) {
      const emptyTerm = createEmptyTerm();
      dispatch(
        addTerm({
          categoryId: selectedEntry.id,
          indexToPlace: 0,
          term: emptyTerm,
        })
      );
    }
    if (selectedEntry.type === entryTypes.TERM) {
      const emptyTerm = createEmptyTerm();
      dispatch(
        addTerm({
          categoryId: selectedEntry.categoryId,
          indexToPlace:
            findTermIndex(
              selectedEntry.id,
              categories.find(
                (category) => category.id === selectedEntry.categoryId
              )
            ) + 1,
          term: emptyTerm,
        })
      );
    }
    setSelectedRowIndex(selectedRowIndex + 1);
    setSelectedColumnIndex(0);
  };

  const handleNewCategoryClick = () => {
    if (!selectedEntry) {
      return;
    }
    if (categories.length === 0) {
      const emptyCategory = createEmptyCategory();
      dispatch(
        addCategory({
          indexToPlace: 0,
          category: emptyCategory,
        })
      );
      setSelectedRowIndex(0);
      setSelectedColumnIndex(0);
      return;
    }
    if (
      selectedEntry.type !== entryTypes.CATEGORY &&
      selectedEntry.type !== entryTypes.TERM
    ) {
      return;
    }
    const categoryId =
      selectedEntry.type === entryTypes.CATEGORY
        ? selectedEntry.id
        : selectedEntry.categoryId;
    const emptyCategory = createEmptyCategory();
    dispatch(
      addCategory({
        indexToPlace: categoryId + 1,
        category: emptyCategory,
      })
    );
    let rowIndex;
    if (selectedEntry.type === entryTypes.CATEGORY) {
      if (!selectedEntry.collapsed) {
        rowIndex = selectedRowIndex + selectedEntry.termsLength + 1;
      } else {
        rowIndex = selectedRowIndex + 1;
      }
    }
    if (selectedEntry.type === entryTypes.TERM) {
      rowIndex =
        selectedRowIndex + (selectedEntry.termsLength - selectedEntry.id);
    }
    setSelectedRowIndex(rowIndex);
    setSelectedColumnIndex(0);
  };

  const handleDescriptionChange = ({ target: { value } }) => {
    dispatch(
      editDictionary((prevDictionary) => ({
        ...prevDictionary,
        description: value,
      }))
    );
  };

  return (
    <div
      className="edit_dictionary_page__sub_header"
      id="id-edit_dictionary_page__sub_header"
      data-testid="dictionary-edit-page-subheader"
    >
      <div className="edit_dictionary_page__sub_header__left_part">
        {(createdBy || createdDate || updateBy || updatedDate) && (
          <div
            className="edit_dictionary_page__sub_header__left_part__relevance"
            data-testid="zxc"
          >
            {(createdBy || createdDate) &&
              `Created${createdBy ? ` by ${createdBy}` : ""}${
                createdDate
                  ? ` on ${new Date(createdDate).toLocaleDateString("en-GB", {
                      dateStyle: "medium",
                    })}`
                  : ""
              }`}
            {(updateBy || updatedDate) &&
              ` | Last updated${updateBy ? ` by ${updateBy}` : ""}${
                updatedDate
                  ? ` on ${new Date(updatedDate).toLocaleDateString("en-GB", {
                      dateStyle: "medium",
                    })}`
                  : ""
              }`}
          </div>
        )}
        <div className="edit_dictionary_page__sub_header__left_part__description">
          {isEditMode ? (
            <input
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter Dictionary Description"
              className="edit_dictionary_page__sub_header__left_part__description__editor"
            />
          ) : (
            <div className="edit_dictionary_page__sub_header__left_part__description__view">
              {description || (
                <span className="edit_dictionary_page__sub_header__left_part__description__view__placeholder">
                  Dictionary description
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {isEditMode && (
        <div className="edit_dictionary_page__sub_header__right_part">
          {renderButton({
            title: "New Category",
            icon: <PlusIcon />,
            onClick: handleNewCategoryClick,
            dataTest: "subheader_new_category_button",
          })}
          <div className="edit_dictionary_page__sub_header__right_part__separator" />
          {renderButton({
            title: "New Term",
            icon: <PlusIcon />,
            onClick: handleNewTermClick,
            dataTest: "subheader_new_term_button",
            disabled: categories.length === 0,
          })}
        </div>
      )}
    </div>
  );
};

export default SubHeader;

SubHeader.propTypes = {
  selectedRowIndex: PropTypes.number,
  setSelectedRowIndex: PropTypes.func,
  setSelectedColumnIndex: PropTypes.func,
};
